/* eslint-disable max-lines */
import { Pipette } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "tailwind-variants";

import { type RefineOperation, useRefineHistoryContext } from "@design-lab/entities/artwork";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { trackImageEditToolUsed } from "@design-lab/shared/lib/analytics";
import { Button } from "@design-lab/shared/ui/design-system/Button";
import { Switch } from "@design-lab/shared/ui/design-system/Switch";
import { processNegativeSpace } from "@design-lab/widgets/artboard/lib/negativeSpaceProcessor";

import { ColorComboBox } from "./ColorComboBox";
import { CSS_COLORS } from "./cssColors";

type NegativeSpaceToolProps = {
  isActive: boolean;
  artworkId?: string;
};

type ProcessingResult = {
  imageData: string;
  mask: Uint8Array;
  stats: {
    removedPixelCount: number;
    eligiblePixelCount: number;
  };
};

// eslint-disable-next-line max-lines-per-function
export function NegativeSpaceTool({ isActive, artworkId }: NegativeSpaceToolProps) {
  const [sensitivity, setSensitivity] = useState(20);
  const [sensitivityInput, setSensitivityInput] = useState("20");
  const [selectedColorForTool, setSelectedColorForTool] = useState("#ffffff");
  const [colorInputValue, setColorInputValue] = useState("white");
  const [previewMask, setPreviewMask] = useState(false);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [isFirstActivation, setIsFirstActivation] = useState(true);

  const [activeResult, setActiveResult] = useState<ProcessingResult | null>(null);

  const { isDesktop } = useResponsive();
  const {
    currentImageData,
    pushOperation,
    updateLastOperation,
    currentOperation,
    entries,
    index,
    setCurrentImageData
  } = useRefineHistoryContext();

  const isAutoApply = true;

  const baselineImageRef = useRef<string | null>(null);

  const isEyeDropperAvailable = useMemo(() => "EyeDropper" in window, []);

  // Helper to get display name from hex
  const getColorDisplayName = useCallback((hex: string): string => {
    const matchingColor = CSS_COLORS.find(c => c.hex.toLowerCase() === hex.toLowerCase());
    return matchingColor ? matchingColor.name : hex;
  }, []);

  // Update colorInputValue when selectedColorForTool changes
  useEffect(() => {
    setColorInputValue(getColorDisplayName(selectedColorForTool));
  }, [selectedColorForTool, getColorDisplayName]);

  // 1. Lifecycle & Cleanup
  useEffect(() => {
    if (!isActive) {
      // Cleanup when tool becomes inactive
      const baselineImage = baselineImageRef.current;
      const shouldRevert = !isAutoApply && baselineImage && hasPendingChanges;
      if (shouldRevert) {
        setCurrentImageData(baselineImage);
      }

      // Reset state
      setHasPendingChanges(false);
      setIsFirstActivation(true);
      setPreviewMask(false);
      setActiveResult(null);
      baselineImageRef.current = null;
      return;
    }

    // Handle first activation
    if (isFirstActivation && currentImageData) {
      baselineImageRef.current = currentImageData;
      setIsFirstActivation(false);
    }
  }, [
    isActive,
    currentImageData,
    isAutoApply,
    hasPendingChanges,
    setCurrentImageData,
    isFirstActivation
  ]);

  // 2. Sync Active Result from History (Auto Apply & Initial Load)
  useEffect(() => {
    if (!isActive) return;

    // If we have pending changes in manual mode, don't overwrite with history
    // (unless history changed due to Undo/Redo which shouldn't happen while editing)
    if (!isAutoApply && hasPendingChanges) return;

    const currentEntry = entries[index];
    if (currentEntry?.payload.type === "negative-space") {
      setActiveResult({
        imageData: currentEntry.payload.imageData,
        mask: currentEntry.payload.metadata?.mask || new Uint8Array(),
        stats: {
          removedPixelCount: currentEntry.payload.metadata?.removedPixels || 0,
          eligiblePixelCount: currentEntry.payload.metadata?.eligiblePixels || 0
        }
      });
    } else if (currentEntry?.payload.imageData) {
      // Baseline or other operation
      setActiveResult({
        imageData: currentEntry.payload.imageData,
        mask: new Uint8Array(),
        stats: { removedPixelCount: 0, eligiblePixelCount: 0 }
      });
    }
  }, [isActive, entries, index, isAutoApply, hasPendingChanges]);

  // 3. Display Effect - Updates Canvas based on Active Result and Mask Toggle
  useEffect(() => {
    if (!isActive || !activeResult) return;

    const updateDisplay = async () => {
      const shouldShowMask = previewMask && activeResult.mask.length > 0;
      if (!shouldShowMask) {
        setCurrentImageData(activeResult.imageData);
        return;
      }

      // Generate mask visualization
      const img = new Image();
      img.src = activeResult.imageData; // Use dimensions from image
      await new Promise<void>(resolve => {
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            resolve();
            return;
          }

          const maskImageData = ctx.createImageData(canvas.width, canvas.height);
          for (let i = 0; i < activeResult.mask.length; i += 1) {
            const pixelIndex = i * 4;
            const value = activeResult.mask[i] === 0 ? 0 : 255; // 0 = removed (black), 255 = kept (white)
            maskImageData.data[pixelIndex] = value;
            maskImageData.data[pixelIndex + 1] = value;
            maskImageData.data[pixelIndex + 2] = value;
            maskImageData.data[pixelIndex + 3] = 255;
          }
          ctx.putImageData(maskImageData, 0, 0);
          setCurrentImageData(canvas.toDataURL("image/png"));
          resolve();
        };
      });
    };

    updateDisplay();
  }, [isActive, activeResult, previewMask, setCurrentImageData]);

  // 4. Processing Logic
  const commitToHistory = useCallback(
    ({
      result,
      color,
      sens,
      isUpdate
    }: {
      result: ProcessingResult;
      color: string;
      sens: number;
      isUpdate: boolean;
    }) => {
      const operation: RefineOperation = {
        type: "negative-space",
        imageData: result.imageData,
        metadata: {
          dimensions: currentOperation?.metadata?.dimensions || { x: 0, y: 0, width: 0, height: 0 },
          selectedColor: color,
          sensitivity: sens,
          removedPixels: result.stats.removedPixelCount,
          eligiblePixels: result.stats.eligiblePixelCount,
          mask: result.mask
        },
        timestamp: Date.now()
      };

      if (isUpdate) {
        updateLastOperation(operation);
      } else {
        pushOperation(operation);
        if (artworkId) {
          trackImageEditToolUsed(artworkId, "negative_space", { hex_color: color });
        }
      }

      // In Manual Mode, update baseline to the new result so next operations build on it
      if (!isAutoApply) {
        baselineImageRef.current = result.imageData;
        setHasPendingChanges(false);
      }
    },
    [currentOperation, updateLastOperation, pushOperation, artworkId, isAutoApply]
  );

  const runTool = useCallback(
    async (color: string, sens: number, isSensitivityChange: boolean) => {
      try {
        // Determine source image
        let sourceImageForProcessing: string;
        const lastEntry = isAutoApply ? entries[index] : null;
        const isLastNegativeSpace = lastEntry?.payload.type === "negative-space";

        if (!isAutoApply) {
          sourceImageForProcessing = baselineImageRef.current || currentImageData!;
        } else {
          const shouldUsePreviousImage = isSensitivityChange && isLastNegativeSpace;

          sourceImageForProcessing = shouldUsePreviousImage
            ? entries[index - 1]?.payload.imageData || currentImageData!
            : lastEntry?.payload.imageData || currentImageData!;
        }

        // Process
        const img = new Image();
        img.src = sourceImageForProcessing;
        await new Promise(resolve => {
          img.onload = resolve;
        });

        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("No context");
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

        const result = processNegativeSpace({
          imageData,
          targetHexColor: color,
          sensitivity: sens
        });

        // Convert result to data URL
        const resultCanvas = document.createElement("canvas");
        resultCanvas.width = result.imageData.width;
        resultCanvas.height = result.imageData.height;
        const resultCtx = resultCanvas.getContext("2d");
        resultCtx?.putImageData(result.imageData, 0, 0);

        const processingResult: ProcessingResult = {
          imageData: resultCanvas.toDataURL("image/png"),
          mask: result.mask,
          stats: result.stats
        };

        // Handle Result
        if (isAutoApply) {
          const isUpdate = isSensitivityChange && isLastNegativeSpace;
          commitToHistory({ result: processingResult, color, sens, isUpdate });
        } else {
          // Manual: Update preview state
          setActiveResult(processingResult);
          setHasPendingChanges(true);
        }
      } catch (error) {
        console.error("Error in runTool:", error);
      }
    },
    [isAutoApply, entries, index, currentImageData, commitToHistory]
  );

  // Event Handlers
  const handleColorInputChange = useCallback((value: string) => {
    setColorInputValue(value);
  }, []);

  const handleColorChange = useCallback(
    (hex: string) => {
      setSelectedColorForTool(hex);
      runTool(hex, sensitivity, false);
    },
    [sensitivity, runTool]
  );

  const handleEyeDropper = useCallback(async () => {
    if (isEyeDropperAvailable) {
      try {
        const eyeDropper = new window.EyeDropper!();
        const result = await eyeDropper.open();
        setSelectedColorForTool(result.sRGBHex);
        setColorInputValue(result.sRGBHex);
        runTool(result.sRGBHex, sensitivity, false);
      } catch (e) {
        // User cancelled
      }
    }
  }, [isEyeDropperAvailable, sensitivity, runTool]);

  const handleSensitivityChange = useCallback(
    (nextValue: string) => {
      if (nextValue === "") {
        setSensitivityInput("");
        return;
      }

      const parsed = Number(nextValue);
      if (Number.isNaN(parsed)) return;
      if (parsed > 100) return;

      const clampedSens = Math.max(0, parsed);
      setSensitivityInput(String(clampedSens));
      setSensitivity(clampedSens);
      runTool(selectedColorForTool, clampedSens, true);
    },
    [selectedColorForTool, runTool]
  );

  const handleSensitivityBlur = useCallback(() => {
    if (sensitivityInput !== "") return;

    setSensitivityInput("0");
    setSensitivity(0);
    runTool(selectedColorForTool, 0, true);
  }, [sensitivityInput, selectedColorForTool, runTool]);

  const handleApply = useCallback(() => {
    if (activeResult) {
      commitToHistory({
        result: activeResult,
        color: selectedColorForTool,
        sens: sensitivity,
        isUpdate: false
      });
    }
  }, [activeResult, commitToHistory, selectedColorForTool, sensitivity]);

  const handlePreviewMaskToggle = useCallback((checked: boolean) => {
    setPreviewMask(checked);
  }, []);

  // UI Rendering
  if (!isActive) return null;

  if (isDesktop) {
    return (
      <div
        className={cn(
          "bg-background absolute left-1/2 z-10 flex -translate-x-1/2 items-center gap-5 rounded-lg border border-gray-200 px-4 py-3 shadow-lg",
          "bottom-30"
        )}
      >
        {/* Pick Color Section */}
        <div className="flex items-center gap-2 overflow-visible">
          <span className="text-right text-xs text-gray-600">Pick a Color</span>
          <ColorComboBox
            value={colorInputValue}
            onChange={handleColorInputChange}
            onColorChange={handleColorChange}
            selectedColorHex={selectedColorForTool}
            dropdownPlacement="left"
            dropdownWidth="w-64"
            size="md"
            className="w-40"
            placeholder="Enter color"
          />

          {/* Eyedropper Button */}
          {isEyeDropperAvailable && (
            <Button
              variant="outline"
              onPress={handleEyeDropper}
              className="h-10 w-10 rounded-lg bg-white p-2"
              data-testid="negative-space-eyedropper"
            >
              <Pipette className="size-5" />
            </Button>
          )}
        </div>

        {/* Sensitivity */}
        <div className="flex items-center gap-2">
          <span className="text-right text-xs text-gray-600">Sensitivity Percentage</span>
          <div className="relative">
            <input
              type="number"
              min={0}
              max={100}
              step={5}
              value={sensitivityInput}
              onChange={e => handleSensitivityChange(e.target.value)}
              onBlur={handleSensitivityBlur}
              className="h-10 w-20 rounded-lg border border-gray-300 bg-white px-3 pr-7 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              data-testid="negative-space-sensitivity-input"
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-600">
              %
            </span>
          </div>
        </div>

        {/* Preview Toggle */}
        <div className="flex items-center gap-2">
          <span className="w-30 text-right text-xs text-gray-600">Preview Removed Background</span>
          <Switch
            isSelected={previewMask}
            onChange={handlePreviewMaskToggle}
            data-testid="preview-mask-toggle"
            size="md"
          />
        </div>

        {/* Apply Button (Manual Apply only) */}
        {!isAutoApply && (
          <Button
            onPress={handleApply}
            className="bg-cta-gradient hover:bg-cta-gradient-hover text-white"
            data-testid="apply-changes"
            isDisabled={!hasPendingChanges}
          >
            Blend Color
          </Button>
        )}
      </div>
    );
  }

  // Mobile Layout
  return (
    <>
      <div
        className={cn(
          "bg-background absolute left-1/2 z-10 flex w-[calc(100%-2rem)] max-w-md -translate-x-1/2 flex-col gap-3 rounded-lg border border-gray-200 p-2 shadow-lg",
          "bottom-26"
        )}
      >
        {/* Row 1: Pick Color + Input + Eyedropper */}
        <div className="flex items-center gap-2">
          <span className="shrink-0 text-xs text-gray-600">Pick a Color</span>
          <ColorComboBox
            value={colorInputValue}
            onChange={handleColorInputChange}
            onColorChange={handleColorChange}
            selectedColorHex={selectedColorForTool}
            dropdownPlacement="bottom"
            size="md"
            className="flex-1"
            placeholder="Enter color"
          />

          {/* Eyedropper Button */}
          {isEyeDropperAvailable && (
            <Button
              variant="outline"
              onPress={handleEyeDropper}
              className="h-10 w-10 shrink-0 rounded-lg bg-white p-2"
              data-testid="negative-space-eyedropper-mobile"
            >
              <Pipette className="size-5" />
            </Button>
          )}
        </div>

        {/* Row 2: Sensitivity + Preview Toggle */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-right text-xs text-gray-600">Sensitivity Percentage</span>
            <div className="relative">
              <input
                type="number"
                min={0}
                max={100}
                step={5}
                value={sensitivityInput}
                onChange={e => handleSensitivityChange(e.target.value)}
                onBlur={handleSensitivityBlur}
                className="w-18 h-10 rounded-lg border border-gray-300 bg-white px-3 pr-7 text-base focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                data-testid="negative-space-sensitivity-input-mobile"
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-base text-gray-600">
                %
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="w-30 text-right text-xs text-gray-600">
              Preview Removed Background
            </span>
            <Switch
              isSelected={previewMask}
              onChange={handlePreviewMaskToggle}
              data-testid="preview-mask-toggle-mobile"
              size="md"
            />
          </div>
        </div>

        {/* Apply Button (Manual Apply only) */}
        {!isAutoApply && (
          <Button
            onPress={handleApply}
            className="bg-cta-gradient hover:bg-cta-gradient-hover w-full text-white"
            data-testid="apply-changes-mobile"
            isDisabled={!hasPendingChanges}
          >
            Blend Color
          </Button>
        )}
      </div>
    </>
  );
}
